// Layout shell — header with logo, [+ New Project], Archive, theme, settings.
// Used as the wrapper for `/`, `/settings`, and (with header hidden) `/review/*`.
// Paper Review owns its own header because it needs project/paper context that
// doesn't belong in the shell.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useSWRConfig } from "swr";

import { CreateProjectModal } from "./CreateProjectModal";
import { GuidedTour } from "./GuidedTour";
import { HeaderActions } from "./HeaderActions";
import { ProjectImport } from "./ProjectImport";
import { ShortcutHelp } from "./ShortcutHelp";
import { Tooltip } from "./Tooltip";
import { WelcomeOverlay } from "./WelcomeOverlay";
import { SettingsModal } from "../views/Settings";
import type { Project } from "../api/types";
import { useKeyboard } from "../hooks/useKeyboard";
import { trackEvent, useTelemetry } from "../telemetry/useTelemetry";
import {
  applyTheme,
  getStoredTheme,
  setStoredTheme,
  type Theme,
} from "../theme";

import "./Layout.css";

const EXPANDED_STORAGE_KEY = "ophelia.tree.expanded";

export function Layout() {
  const { t } = useTranslation();
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());
  const [modalOpen, setModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { mutate } = useSWRConfig();
  useTelemetry();

  useEffect(() => {
    trackEvent("app.load");
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Paper review supplies its own header (project name, paper nav, page info).
  const isReview = location.pathname.startsWith("/review/");
  const isNavigator = location.pathname === "/";
  const archiveActive = params.get("archived") === "1";

  const bindings = useMemo(
    () => [
      {
        key: "?",
        shift: true,
        handler: () => {
          setHelpOpen(true);
          trackEvent("shortcut.help.open");
        },
      },
      { key: "Escape", handler: () => setHelpOpen(false) },
      {
        key: "t",
        handler: () => {
          const next: Theme = theme === "light" ? "dark" : "light";
          setThemeState(next);
          setStoredTheme(next);
        },
      },
      {
        key: "n",
        meta: true,
        handler: (e: KeyboardEvent) => {
          e.preventDefault();
          setModalOpen(true);
        },
      },
    ],
    [theme],
  );
  useKeyboard(bindings);

  function toggleArchive() {
    const next = new URLSearchParams(params);
    if (archiveActive) {
      next.delete("archived");
    } else {
      next.set("archived", "1");
      next.delete("project");
      next.delete("paper");
    }
    setParams(next, { replace: true });
  }

  function onProjectCreated(project: Project) {
    try {
      const raw = window.localStorage.getItem(EXPANDED_STORAGE_KEY);
      const map = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
      map[String(project.id)] = true;
      window.localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(map));
    } catch {
      // localStorage write failures are non-fatal.
    }

    setModalOpen(false);
    void mutate("/api/projects/?archived=0");
    if (location.pathname !== "/") {
      navigate(`/?project=${project.id}`);
    } else {
      navigate(`/?project=${project.id}`, { replace: true });
    }
  }

  return (
    <div className="app-shell">
      <WelcomeOverlay />
      <GuidedTour />
      {!isReview && (
        <header className="app-header">
          <Tooltip tipKey="header_logo_home">
            <Link to="/" className="app-header__logo" aria-label={t("header.logo_alt")}>
              <img src="/logo.svg" alt="" width={28} height={28} />
              <span className="app-header__wordmark">ophelia</span>
            </Link>
          </Tooltip>

          <div className="app-header__spacer" />

          <Tooltip tipKey="header_new_project">
            <button
              type="button"
              className="app-header__btn app-header__btn--primary"
              onClick={() => setModalOpen(true)}
              aria-label={t("header.new_project")}
            >
              +<span className="app-header__btn-label">{t("header.new_project")}</span>
            </button>
          </Tooltip>

          {isNavigator && <ProjectImport />}

          {isNavigator && (
            <Tooltip tipKey={archiveActive ? "header_archive_hide" : "header_archive_show"}>
              <button
                type="button"
                className={
                  "app-header__btn app-header__btn--outline" +
                  (archiveActive ? " app-header__btn--active" : "")
                }
                onClick={toggleArchive}
                aria-label={t(archiveActive ? "header.archived_view" : "header.archive")}
              >
                <IconBox /><span className="app-header__btn-label">{t("header.archive")}</span>
              </button>
            </Tooltip>
          )}

          <HeaderActions onSettingsOpen={() => setSettingsOpen(true)} />
        </header>
      )}

      <main className="app-main">
        <Outlet />
      </main>

      {modalOpen && (
        <CreateProjectModal
          onClose={() => setModalOpen(false)}
          onCreated={onProjectCreated}
        />
      )}
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      {helpOpen && <ShortcutHelp onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function IconBox() {
  return (
    <svg width="13" height="12" viewBox="0 0 13 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ display: "inline-block", verticalAlign: "middle" }}>
      <rect x="1" y="4.5" width="11" height="7" rx="1" />
      <path d="M1 4.5h11V3a.5.5 0 0 0-.5-.5H1.5A.5.5 0 0 0 1 3v1.5z" />
      <path d="M4.5 8h4" />
    </svg>
  );
}
